import { useEffect, type FC } from "react";
import { I18nextProvider } from "react-i18next";
import { Outlet } from "react-router-dom";
import i18n from "@/i18n/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@ui/Sonner";
import { LanguageProvider, ThemeProvider } from "@/providers";
import Layout from "@/components/Layout";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

const App: FC = () => {
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let rafId: number;

    function sendHeight() {
      // Use requestAnimationFrame to ensure layout is complete
      rafId = requestAnimationFrame(() => {
        // Add small delay to account for animations/transitions
        timeoutId = setTimeout(() => {
          const height = document.documentElement.scrollHeight || document.body.scrollHeight;
          window.parent.postMessage({ height }, "*");
        }, 50);
      });
    }

    // Send on mount
    sendHeight();

    // Watch for DOM changes (new form steps, content changes)
    const mutationObserver = new MutationObserver(() => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      sendHeight();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "data-*"],
    });

    // Watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      sendHeight();
    });
    resizeObserver.observe(document.documentElement);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <ThemeProvider>
            <Layout>
              <Outlet />
            </Layout>
            <Toaster />
          </ThemeProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
};
export default App;
